import { createSlice, createSelector } from '@reduxjs/toolkit';
import { SimpleSupplyPoint, SupplyComponent, SupplyFeeder, COMPONENT_KINDS, FEEDER_KINDS } from '../types/SimpleSupplyPoint';
import { SimpleElectricalService } from '../modules/panel';
import { ElectricalUpdateService } from '../modules/panel';
import { generateUniqueId } from '../utils/generateUniqueId';

/**
 * Simple SupplyPoint Redux Slice
 * Legacy supplySlice'a alternatif - çok daha basit ve performant
 */

const initialState = {
  // Ana veri yapısı - sadece 2 array!
  feeders: [],
  components: [],

  // Metadata
  metadata: {
    version: "1.0",
    createdAt: null,
    updatedAt: null,
    migratedFrom: null
  },

  // UI state (gerekirse)
  selectedComponentId: null,
  selectedFeederId: null,

  // Performance tracking
  lastCalculationTime: 0,
  calculationCount: 0,

  // Document update trigger
  updateCounter: 0
};

const simpleSupplySlice = createSlice({
  name: 'simpleSupply',
  initialState,
  reducers: {

    // ===============================
    // BASIC CRUD OPERATIONS
    // ===============================

    /**
     * Tüm supply point'i set et
     */
    setSimpleSupplyPoint: (state, action) => {
      const { feeders, components, metadata } = action.payload;
      state.feeders = feeders || [];
      state.components = components || [];
      state.metadata = {
        ...state.metadata,
        ...metadata,
        updatedAt: new Date().toISOString()
      };
    },

    /**
     * Component ekleme
     */
    addComponent: (state, action) => {
      const componentData = action.payload;

      // ✅ Duplicate kontrolü - aynı ID'li component varsa ekleme
      if (componentData.id && state.components.some(c => c.id === componentData.id)) {
        return;
      }

      // ✅ kind normalizasyonu - 'LOAD' → 'load', 'PANEL' → 'panel' vs.
      // Hook'lardan gelen büyük harfli kind'ları COMPONENT_KINDS ile uyumlu hale getir
      let normalizedKind = componentData.kind;
      if (typeof normalizedKind === 'string') {
        const kindLower = normalizedKind.toLowerCase();
        // COMPONENT_KINDS değerleriyle eşleştir
        if (kindLower === 'load') normalizedKind = COMPONENT_KINDS.LOAD;
        else if (kindLower === 'panel') normalizedKind = COMPONENT_KINDS.PANEL;
        else if (kindLower === 'dist_panel') normalizedKind = COMPONENT_KINDS.DIST_PANEL;
        else if (kindLower === 'meter_panel') normalizedKind = COMPONENT_KINDS.METER_PANEL;
        else if (kindLower === 'supply') normalizedKind = COMPONENT_KINDS.SUPPLY;
        else if (kindLower === 'branch') normalizedKind = COMPONENT_KINDS.BRANCH;
      }

      // ✅ Direkt olarak componentData'yı push et (spread ile deep copy)
      state.components.push({
        ...componentData,
        kind: normalizedKind, // ✅ Normalize edilmiş kind kullan
        electrical: { ...componentData.electrical },
        entries: componentData.entries ? [...componentData.entries] : [],
        outs: componentData.outs ? [...componentData.outs] : [],
        incomingFeeders: [...(componentData.incomingFeeders || [])],
        outgoingFeeders: [...(componentData.outgoingFeeders || [])],
        metadata: { ...componentData.metadata },
        position: componentData.position ? { ...componentData.position } : null,
        rotation: componentData.rotation ? { ...componentData.rotation } : null
      });

      const addedComponent = state.components[state.components.length - 1];

      // ✅ Parent component'in outs dizisine de ekle (eğer parent varsa)
      if (addedComponent.parentComponentId) {
        const parentComponent = state.components.find(c => c.id === addedComponent.parentComponentId);
        if (parentComponent) {
          if (!parentComponent.outs) {
            parentComponent.outs = [];
          }

          // Outs dizisinde zaten var mı kontrol et
          const existingOut = parentComponent.outs.find(out =>
            out.targetComponentId === addedComponent.id
          );

          if (!existingOut) {
            const outEntry = {
              targetComponentId: addedComponent.id,
              targetComponentName: addedComponent.name,
              branchType: addedComponent.kind === 'meter_panel' || addedComponent.kind === 'dist_panel' || addedComponent.kind === 'panel' ? 'panel' : 'component',
              description: addedComponent.name || `${addedComponent.kind} connection`,
              power: addedComponent.electrical?.installedPower || 0,
              mcbReference: parentComponent.outs.length + 1,
              // Feeder henüz eklenmediği için placeholder
              feederId: null,
              branchID: null
            };

            parentComponent.outs.push(outEntry);
           }
        }
      }

      state.metadata.updatedAt = new Date().toISOString();

    },

    /**
     * Component güncelleme
     */
    updateComponent: (state, action) => {
      const { id, changes } = action.payload;
      const componentIndex = state.components.findIndex(c => c.id === id);

      if (componentIndex !== -1) {
        state.components[componentIndex] = {
          ...state.components[componentIndex],
          ...changes,
          electrical: {
            ...state.components[componentIndex].electrical,
            ...changes.electrical
          },
          // ✅ metadata'yı da merge et (overwrite yerine)
          metadata: {
            ...state.components[componentIndex].metadata,
            ...changes.metadata
          }
        };
        state.metadata.updatedAt = new Date().toISOString();

        // Dokümanların güncellenmesi için counter'ı artır
        state.updateCounter += 1;
      }
    },

    /**
     * Component silme
     */
    removeComponent: (state, action) => {
      const componentId = action.payload;

      // Component'i sil
      const componentIndex = state.components.findIndex(c => c.id === componentId);
      if (componentIndex !== -1) {
        const removedComponent = state.components[componentIndex];
        state.components.splice(componentIndex, 1);

        // 🆕 İnkremental güç propagasyonu - Negatif güç ekle (güç çıkarma)
        const removedPower = removedComponent.electrical?.installedPower || 0;
        if (removedPower > 0) {
          simpleSupplySlice.caseReducers.incrementalPowerUpdate(state, {
            payload: {
              startComponentId: componentId,
              powerDelta: -removedPower // Negatif değer = güç çıkarma
            }
          });
        }

        // Parent component'in outs dizisinden ilgili çıkışı sil
        const incomingFeeder = state.feeders.find(f => f.to === componentId);
        if (incomingFeeder) {
          const parentComponent = state.components.find(c => c.id === incomingFeeder.from);
          if (parentComponent && parentComponent.outs) {
            // Load'ın outs dizisinden çıkarma
            const outIndex = parentComponent.outs.findIndex(out => out.loadId === componentId);
            if (outIndex !== -1) {
              parentComponent.outs.splice(outIndex, 1);
            }
          }
        }

        // İlgili feeder'ları da sil
        state.feeders = state.feeders.filter(f =>
          f.from !== componentId && f.to !== componentId
        );

        // Diğer component'lerin feeder referanslarını temizle
        state.components.forEach(component => {
          component.incomingFeeders = component.incomingFeeders.filter(fId => {
            const feeder = state.feeders.find(f => f.id === fId);
            return feeder && feeder.from !== componentId;
          });
          component.outgoingFeeders = component.outgoingFeeders.filter(fId => {
            const feeder = state.feeders.find(f => f.id === fId);
            return feeder && feeder.to !== componentId;
          });
        });

        state.metadata.updatedAt = new Date().toISOString();
      }
    },

    /**
     * Feeder ekleme
     */
    addFeeder: (state, action) => {
      const feederData = action.payload;

      // ✅ Duplicate kontrolü - aynı ID'li feeder varsa ekleme
      if (feederData.id && state.feeders.some(f => f.id === feederData.id)) {
        return;
      }

      // Create plain object instead of class instance to avoid serialization issues
      // ✅ Deep copy ile referans sorunlarını önle
      const newFeeder = {
        ...feederData,
        id: feederData.id || generateUniqueId('feeder'),
        sourceId: feederData.sourceId || feederData.from,
        targetId: feederData.targetId || feederData.to,
        from: feederData.from || feederData.sourceId,
        to: feederData.to || feederData.targetId,
        kind: feederData.kind || FEEDER_KINDS.MAIN_FEEDER,
        vertices: feederData.vertices ? [...feederData.vertices] : [],
        electrical: feederData.electrical ? { ...feederData.electrical } : {},
        cable: feederData.cable ? { ...feederData.cable } : {},
        metadata: feederData.metadata ? { ...feederData.metadata } : {},
        // ✅ Protection deep copy - RCD/MCB referans sorunu için kritik!
        protection: feederData.protection ? {
          ...feederData.protection,
          mcb: feederData.protection.mcb ? { ...feederData.protection.mcb } : undefined,
          rcd: feederData.protection.rcd ? { ...feederData.protection.rcd } : undefined
        } : undefined,
        wireId: feederData.wireId
      };

      state.feeders.push(newFeeder);

      // Component relationship'leri güncelle
      const fromComponent = state.components.find(c => c.id === newFeeder.from);
      const toComponent = state.components.find(c => c.id === newFeeder.to);

      if (fromComponent && !fromComponent.outgoingFeeders.includes(newFeeder.id)) {
        fromComponent.outgoingFeeders.push(newFeeder.id);

        // ✅ outs dizisine de ekle (legacy compatibility için)
        if (!fromComponent.outs) {
          fromComponent.outs = [];
        }

        // Önce mevcut component entry'yi bul ve güncelle
        let existingOutIndex = fromComponent.outs.findIndex(out =>
          out.targetComponentId === newFeeder.to
        );

        if (existingOutIndex !== -1) {
          // Mevcut entry'yi güncelle
          const existingOut = fromComponent.outs[existingOutIndex];
          fromComponent.outs[existingOutIndex] = {
            ...existingOut,
            feederId: newFeeder.id,
            branchID: newFeeder.id,
            length: newFeeder.cable?.length || 0,
            power: newFeeder.electrical?.installedPower || 0
          };
         } else {
          // Yeni entry oluştur
          const outEntry = {
            feederId: newFeeder.id,
            branchID: newFeeder.id,
            targetComponentId: newFeeder.to,
            targetComponentName: toComponent?.name || 'Panel',
            branchType: newFeeder.kind === 'panel_feeder' ? 'panel' : 'feeder',
            description: toComponent?.name || `${newFeeder.kind} connection`,
            length: newFeeder.cable?.length || 0,
            power: newFeeder.electrical?.installedPower || 0,
            mcbReference: fromComponent.outs.length + 1 // MCB sıra numarası
          };

          fromComponent.outs.push(outEntry);
          
        }
      }

      if (toComponent && !toComponent.incomingFeeders.includes(newFeeder.id)) {
        toComponent.incomingFeeders.push(newFeeder.id);
      }

      state.metadata.updatedAt = new Date().toISOString();
    },

    /**
     * Feeder güncelleme
     */
    updateFeeder: (state, action) => {
      const { id, changes } = action.payload;
      const feederIndex = state.feeders.findIndex(f => f.id === id);

      if (feederIndex !== -1) {
        state.feeders[feederIndex] = {
          ...state.feeders[feederIndex],
          ...changes,
          power: {
            ...state.feeders[feederIndex].power,
            ...changes.power
          },
          cable: {
            ...state.feeders[feederIndex].cable,
            ...changes.cable
          },
          protection: changes.protection ? {
            ...state.feeders[feederIndex].protection,
            ...changes.protection,
            mcb: changes.protection.mcb ? {
              ...state.feeders[feederIndex].protection?.mcb,
              ...changes.protection.mcb
            } : state.feeders[feederIndex].protection?.mcb,
            rcd: changes.protection.rcd ? {
              ...state.feeders[feederIndex].protection?.rcd,
              ...changes.protection.rcd
            } : state.feeders[feederIndex].protection?.rcd
          } : state.feeders[feederIndex].protection
        };

        // ✅ Feeder'ın protection'ı güncellendiğinde toComponent'in entries'ini de güncelle
        const updatedFeeder = state.feeders[feederIndex];
        const toComponent = state.components.find(c => c.id === updatedFeeder.to);
 
        if (toComponent && updatedFeeder.protection) {
          const existingEntries = toComponent.entries || [];
          const newEntries = [];

          // entries[0] = feeder.protection.mcb + mevcut kind
          if (updatedFeeder.protection.mcb) {
            const existingMCB = existingEntries[0];
            const mcbEntry = {
              kind: existingMCB?.kind || 'main_breaker',  // ✅ kind sabit kalır
              type: 'MCB',                                  // ✅ type her zaman 'MCB'
              indexNo: updatedFeeder.protection.mcb.indexNo,   // ✅ Hesaplanan indexNo
              current: updatedFeeder.protection.mcb.current,   // ✅ Seçilen MCB'den
              polar: updatedFeeder.protection.mcb.polar,       // ✅ Seçilen MCB'den
              title: updatedFeeder.protection.mcb.title,       // ✅ Seçilen MCB'den
              curve: updatedFeeder.protection.mcb.curve,       // Template'den
              neutralBreaking: updatedFeeder.protection.mcb.neutralBreaking  // Template'den
            };
            newEntries.push(mcbEntry);
          }

          // entries[1] = feeder.protection.rcd + mevcut kind
          if (updatedFeeder.protection.rcd) {
            const existingRCD = existingEntries[1];
            const rcdEntry = {
              kind: existingRCD?.kind || 'main_protective_device',  // ✅ kind sabit kalır
              type: 'RCD',                                           // ✅ type her zaman 'RCD'
              indexNo: updatedFeeder.protection.rcd.indexNo,        // ✅ Hesaplanan indexNo
              current: updatedFeeder.protection.rcd.current,        // ✅ Seçilen RCD'den
              polar: updatedFeeder.protection.rcd.polar,            // ✅ Seçilen RCD'den
              title: updatedFeeder.protection.rcd.title,            // ✅ Seçilen RCD'den
              sensitivity: updatedFeeder.protection.rcd.sensitivity // Template'den
            };
             newEntries.push(rcdEntry);
          }

          toComponent.entries = newEntries;
         }

        state.metadata.updatedAt = new Date().toISOString();

      }
    },

    /**
     * Feeder silme
     */
    removeFeeder: (state, action) => {
      const feederId = action.payload;

      const feederIndex = state.feeders.findIndex(f => f.id === feederId);
      if (feederIndex !== -1) {
        const removedFeeder = state.feeders[feederIndex];
        state.feeders.splice(feederIndex, 1);

        // Component'lerin feeder referanslarını temizle
        const fromComponent = state.components.find(c => c.id === removedFeeder.from);
        const toComponent = state.components.find(c => c.id === removedFeeder.to);

        if (fromComponent) {
          fromComponent.outgoingFeeders = fromComponent.outgoingFeeders.filter(fId => fId !== feederId);
        }

        if (toComponent) {
          toComponent.incomingFeeders = toComponent.incomingFeeders.filter(fId => fId !== feederId);
        }

        state.metadata.updatedAt = new Date().toISOString();
      }
    },

    // ===============================
    // ELECTRICAL OPERATIONS
    // ===============================

    /**
     * İnkremental güç propagasyonu - Parent chain'e güç ekleme/çıkarma
     * @param {object} action.payload - { startComponentId, powerDelta }
     */
    incrementalPowerUpdate: (state, action) => {
      const { startComponentId, powerDelta } = action.payload;


            // Simple supply point instance oluştur
      const simpleSupply = new SimpleSupplyPoint({
        feeders: state.feeders,
        components: state.components
      });

      // İnkremental güç propagasyonu hesapla
      const updatedComponents = SimpleElectricalService.incrementalPowerPropagation(
        startComponentId,
        powerDelta,
        simpleSupply
      );

      // Redux state'i güncelle
      updatedComponents.forEach(update => {
        const componentIndex = state.components.findIndex(c => c.id === update.componentId);
        if (componentIndex !== -1) {
          state.components[componentIndex].electrical = {
            ...state.components[componentIndex].electrical,
            ...update.electrical
          };
        }
      });

      state.metadata.updatedAt = new Date().toISOString();

      // Dokümanların güncellenmesi için counter'ı artır
      state.updateCounter += 1;
    },

    /**
     * Tüm component'lerin electrical değerlerini yeniden hesapla
     */
    recalculateAllElectrical: (state) => {
      const startTime = performance.now();

      // Simple supply point instance oluştur
      const simpleSupply = new SimpleSupplyPoint({
        feeders: state.feeders,
        components: state.components
      });

      // Hesapla
      const results = SimpleElectricalService.recalculateAllComponents(simpleSupply);

      // Sonuçları state'e uygula
      results.forEach(result => {
        const componentIndex = state.components.findIndex(c => c.id === result.componentId);
        if (componentIndex !== -1) {
          state.components[componentIndex].electrical = {
            ...state.components[componentIndex].electrical,
            ...result.electrical
          };
        }
      });

      // Feeder'ları da güncelle
      state.feeders.forEach(feeder => {
        const feederElectrical = SimpleElectricalService.calculateFeederElectrical(feeder.id, simpleSupply);
        if (feederElectrical) {
          feeder.power = {
            ...feeder.power,
            ...feederElectrical
          };
        }
      });

      // Performance tracking
      const endTime = performance.now();
      state.lastCalculationTime = endTime - startTime;
      state.calculationCount += 1;
      state.metadata.updatedAt = new Date().toISOString();

    },

    /**
     * Belirli component'ten upstream chain'i yeniden hesapla
     * ✅ ENHANCEment: Kablo/sigorta güncellemesi de dahil
     */
    recalculateUpstreamChain: (state, action) => {
      const startComponentId = action.payload;
      const startTime = performance.now();

      const simpleSupply = new SimpleSupplyPoint({
        feeders: state.feeders,
        components: state.components
      });

      // Upstream chain'i hesapla (supply box'a kadar)
      const results = SimpleElectricalService.calculateUpstreamChain(startComponentId, simpleSupply);

      // Sonuçları uygula - güç değerleri
      results.forEach(result => {
        const componentIndex = state.components.findIndex(c => c.id === result.componentId);
        if (componentIndex !== -1) {
          const oldElectrical = state.components[componentIndex].electrical;
          const newElectrical = {
            ...oldElectrical,
            ...result.electrical
          };

          state.components[componentIndex].electrical = newElectrical;


          // 🆕 AKYM DEĞİŞİMİ KONTROLÜ: Current değişimi büyükse cable/breaker güncelle
          const currentChanged = Math.abs((newElectrical.current || 0) - (oldElectrical.current || 0)) > 1;
          const powerChanged = Math.abs((newElectrical.installedPower || 0) - (oldElectrical.installedPower || 0)) > 100;

          if (currentChanged || powerChanged) {

            // Component'in entries dizisindeki MCB'leri güncelle
            const component = state.components[componentIndex];
            if (component.entries && component.entries.length > 0) {
              component.entries.forEach(entry => {
                if (entry.type === 'breaker' && entry.MCB_No !== undefined) {
                  // Yeni akıma göre MCB kontrolü
                  const requiredCurrent = newElectrical.current || 0;
                  const currentMCB = entry.MCB_No;

                   let newMCBNo = currentMCB;
                  if (requiredCurrent > 16 && currentMCB === 433) {
                    newMCBNo = 434; // 16A'dan 20A'ya çık
                  } else if (requiredCurrent > 20 && currentMCB === 434) {
                    newMCBNo = 435; // 20A'dan 25A'ya çık
                  } else if (requiredCurrent <= 16 && currentMCB > 433) {
                    newMCBNo = 433; // Aşağı indir
                  }

                  if (newMCBNo !== currentMCB) {
                     entry.MCB_No = newMCBNo;
                  }
                }
              });
            }
          }
        }
      });

      // İlgili feeder'ları güncelle
      const affectedFeederIds = new Set();
      results.forEach(result => {
        const component = state.components.find(c => c.id === result.componentId);
        if (component) {
          component.outgoingFeeders.forEach(fId => affectedFeederIds.add(fId));
          component.incomingFeeders.forEach(fId => affectedFeederIds.add(fId));
        }
      });

      affectedFeederIds.forEach(feederId => {
        const feederElectrical = SimpleElectricalService.calculateFeederElectrical(feederId, simpleSupply);
        const feederIndex = state.feeders.findIndex(f => f.id === feederId);
        if (feederIndex !== -1 && feederElectrical) {
          const oldPower = state.feeders[feederIndex].power;
          const newPower = {
            ...oldPower,
            ...feederElectrical
          };

          state.feeders[feederIndex].power = newPower;

          // 🆕 KABLO KAPASİTE KONTROLÜ
          const feeder = state.feeders[feederIndex];
          const currentCapacity = feeder.cable?.currentCap || 0;
          const requiredCurrent = newPower.current || 0;

          if (requiredCurrent > currentCapacity * 0.8) { // %80 emniyet faktörü
 
            // Cable upgrade logic burada implementar edilebilir
            // FeederUpdateService.selectNewCable() benzeri
          }
        }
      });

      const endTime = performance.now();
      state.lastCalculationTime = endTime - startTime;
      state.calculationCount += 1;
      state.metadata.updatedAt = new Date().toISOString();

     },

    // ===============================
    // HIGH-LEVEL OPERATIONS
    // ===============================

    /**
     * Yeni load ekle (component + feeder + electrical update)
     */
    addLoadToComponent: (state, action) => {
       const { parentComponentId, loadData, feederData, updateExisting, addPosition } = action.payload;
       // Eğer updateExisting true ise, sadece mevcut component'i güncelle
      if (updateExisting) {
        const existingComponent = state.components.find(c => c.id === loadData.id);
        if (existingComponent) {
          // Eski değerleri kaydet
          const oldInstalledPower = existingComponent.electrical?.installedPower || 0;

          // Component'i güncelle
          existingComponent.electrical = loadData.electrical;
          existingComponent.metadata = loadData.metadata;

          // socketPower her zaman installedPower'a eşit olmalı
          existingComponent.socketPower = loadData.electrical?.installedPower || 0;

          // Parent component'in toplam yükünü güncelle
          const powerDifference = (loadData.electrical?.installedPower || 0) - oldInstalledPower;
          if (powerDifference !== 0 && parentComponentId) {
            const parentComponent = state.components.find(c => c.id === parentComponentId);
            if (parentComponent && parentComponent.electrical) {
              parentComponent.electrical.installedPower = (parentComponent.electrical.installedPower || 0) + powerDifference;
              parentComponent.electrical.demandPower = (parentComponent.electrical.demandPower || 0) + powerDifference;
              parentComponent.electrical.current = parentComponent.electrical.installedPower / (220 * (parentComponent.electrical.phaseCount || 1));
              parentComponent.electrical.socketPower = (parentComponent.electrical.installedPower || 0) + powerDifference;
 
            }
          } 
          // Dokümanların güncellenmesi için counter'ı artır
          state.updateCounter += 1;
          return;
        }
      }

      // feederData null ise sadece component oluştur
      if (!feederData) {
         return;
      }

      // Load component'i oluştur
      const newLoad = new SupplyComponent({
        ...loadData,
        kind: COMPONENT_KINDS.LOAD,
        parentComponentId: parentComponentId,
        incomingFeeders: [feederData.id], // ✅ Feeder ID'si burada set ediliyor
        socketPower: loadData.electrical?.installedPower || 0
      });

      // ✅ Feeder'ı da ekle (addFeeder'daki duplicate check sayesinde 2 kez eklenmeyecek)
      // Bu, cmd.execute() çağırmayan hook'lar (socket, lighting, bell) için gerekli
      if (feederData.id && !state.feeders.some(f => f.id === feederData.id)) {
        const newFeeder = {
          ...feederData,
          id: feederData.id || generateUniqueId('feeder'),
          sourceId: feederData.sourceId || feederData.from,
          targetId: feederData.targetId || feederData.to,
          from: feederData.from || feederData.sourceId,
          to: feederData.to || feederData.targetId,
          kind: feederData.kind || FEEDER_KINDS.BRANCH_CIRCUIT,
          vertices: feederData.vertices ? [...feederData.vertices] : [],
          electrical: feederData.electrical ? { ...feederData.electrical } : {},
          cable: feederData.cable ? { ...feederData.cable } : {},
          metadata: feederData.metadata ? { ...feederData.metadata } : {},
          protection: feederData.protection ? {
            ...feederData.protection,
            mcb: feederData.protection.mcb ? { ...feederData.protection.mcb } : undefined,
            rcd: feederData.protection.rcd ? { ...feederData.protection.rcd } : undefined
          } : undefined,
          wireId: feederData.wireId
        };
        state.feeders.push(newFeeder);

        // Parent component'in outgoingFeeders'ına ekle
        const parentComponent = state.components.find(c => c.id === parentComponentId);
        if (parentComponent && !parentComponent.outgoingFeeders.includes(newFeeder.id)) {
          parentComponent.outgoingFeeders.push(newFeeder.id);
        }
      }

      // ✅ exitOrder metadata'sını hesapla ve ekle
      // Bu sıra numarası doküman oluşturma sırasında kullanılacak
      const parentSiblings = state.components.filter(c =>
        c.kind === COMPONENT_KINDS.LOAD && c.parentComponentId === parentComponentId
      );

      let newExitOrder;
      if (addPosition === 'sona ekle') {
        // "Sona ekle" → exitOrder = 1 (en üst sıra)
        // Mevcut sibling'lerin exitOrder'ını +1 artır
        parentSiblings.forEach(sibling => {
          if (sibling.metadata?.exitOrder) {
            sibling.metadata.exitOrder += 1;
          }
        });
        newExitOrder = 1;
      } else {
        // "Başa ekle" (default) → exitOrder = max + 1 (en alt sıra)
        const maxOrder = parentSiblings.reduce((max, sibling) => {
          const order = sibling.metadata?.exitOrder || 0;
          return Math.max(max, order);
        }, 0);
        newExitOrder = maxOrder + 1;
      }

      // Load'a exitOrder ve addDirection metadata'sını ekle
      if (!newLoad.metadata) newLoad.metadata = {};
      newLoad.metadata.exitOrder = newExitOrder;
      newLoad.metadata.addDirection = addPosition || 'başa ekle'; // "başa ekle" veya "sona ekle"

      // State'e SADECE component ekle (feeder addFeeder reducer'ı tarafından eklenecek)
      state.components.push(newLoad);

      // ✅ Feeder ve outgoingFeeders/outs güncelleme addFeeder reducer'ına taşındı
      // BaseWiringCommand.execute() içinde addFeeder dispatch edilecek

      // Parent component'in electrical alanını hazırla (güç propagasyonu için)
      const parentComponent = state.components.find(c => c.id === parentComponentId);
      if (parentComponent && !parentComponent.electrical) {
        parentComponent.electrical = {};
      }

      state.metadata.updatedAt = new Date().toISOString();

      // 🆕 İnkremental güç propagasyonu - Parent zincire güç ekle
      simpleSupplySlice.caseReducers.incrementalPowerUpdate(state, {
        payload: {
          startComponentId: newLoad.id,
          powerDelta: loadData.electrical?.installedPower || loadData.power || 0 // ✅ FIX: installedPower'ı da kontrol et
        }
      });

      // 🆕 Sadece elektriksel hesaplamalar için upstream chain'i hesapla
      simpleSupplySlice.caseReducers.recalculateUpstreamChain(state, { payload: newLoad.id });

      // 🆕 CASCADE ELEKTRIKSEL GÜNCELLEME - Power propagation sonrası tüm upstream panel'ları kontrol et
      const simpleSupplyState = { components: state.components, feeders: state.feeders };

      // Upstream chain'deki tüm panel'ları bul (power propagation'dan etkilenen)
      const upstreamPanels = [];
      let currentPanel = parentComponent;

      while (currentPanel) {
        // Panel türü kontrolü - tüm panel türlerini dahil et
        const isPanelType = currentPanel && (
          currentPanel.kind === COMPONENT_KINDS.MAIN_PANEL ||
          currentPanel.kind === COMPONENT_KINDS.METER_PANEL ||
          currentPanel.kind === COMPONENT_KINDS.DIST_PANEL ||
          currentPanel.kind === COMPONENT_KINDS.PANEL ||
          currentPanel.type === 'panel' ||
          currentPanel.type === 'dist_panel' ||
          currentPanel.type === 'meter_panel' ||
          currentPanel.type === 'main_panel'
        );

        if (isPanelType) {
          upstreamPanels.push(currentPanel);
        }

        // Bir sonraki upstream panel'i bul (bu panel'e feeder gönderen panel)
        const incomingFeeder = state.feeders.find(f => f.to === currentPanel?.id);
        currentPanel = incomingFeeder ? state.components.find(c => c.id === incomingFeeder.from) : null;
      }
      // Her upstream panel için elektriksel kontrolü yap
      upstreamPanels.forEach((panel, index) => {
        const incomingFeeder = state.feeders.find(f => f.to === panel.id);

        if (incomingFeeder) {
  
          const updateResults = ElectricalUpdateService.calculateElectricalUpdates(
            panel,
            incomingFeeder,
            simpleSupplyState
          );

          // Gerekirse güncellemeleri uygula
          if (updateResults.needsFeederUpdate || updateResults.needsBreakerUpdate || updateResults.newRCD) {
            const updateActions = ElectricalUpdateService.generateUpdateActions(
              panel,
              incomingFeeder,
              updateResults
            );

 
            // Component güncellemelerini uygula
            updateActions.componentUpdates.forEach(update => {
              const componentToUpdate = state.components.find(c => c.id === update.id);
              if (componentToUpdate) {
                Object.assign(componentToUpdate, update.changes);
              }
            });

            // Feeder güncellemelerini uygula
            updateActions.feederUpdates.forEach(update => {
              const feederToUpdate = state.feeders.find(f => f.id === update.id);
              if (feederToUpdate) {
                Object.assign(feederToUpdate, update.changes);
              }
            });

   
          } else {
            
          }
        }
      });
    },

    /**
     * Legacy data'dan migration
     */
    migrateFromLegacy: (state, action) => {
      const legacySupply = action.payload;


      // Transform legacy → simple (bu fonksiyon ayrı implementasyona ihtiyaç duyacak)
      // Şimdilik basit placeholder
      state.components = [];
      state.feeders = [];
      state.metadata = {
        ...state.metadata,
        migratedFrom: "legacy",
        updatedAt: new Date().toISOString()
      };

    },

    // ===============================
    // UI STATE
    // ===============================

    selectComponent: (state, action) => {
      state.selectedComponentId = action.payload;
    },

    selectFeeder: (state, action) => {
      state.selectedFeederId = action.payload;
    },

    clearSelection: (state) => {
      state.selectedComponentId = null;
      state.selectedFeederId = null;
    },

    // ✅ PERFORMANS: Toplu component/feeder ekleme (Wire çizimi Enter'da)
    addBulk: (state, action) => {
      const { components = [], feeders = [] } = action.payload;

      // Component'leri toplu ekle
      components.forEach(componentData => {
        // addComponent reducer'ındaki mantığı kopyala (ama console.log olmadan)
        state.components.push({
          ...componentData,
          electrical: { ...componentData.electrical },
          entries: componentData.entries ? [...componentData.entries] : [],
          outs: componentData.outs ? [...componentData.outs] : [],
          incomingFeeders: [...(componentData.incomingFeeders || [])],
          outgoingFeeders: [...(componentData.outgoingFeeders || [])],
          metadata: { ...componentData.metadata },
          position: componentData.position ? { ...componentData.position } : null,
          rotation: componentData.rotation ? { ...componentData.rotation } : null
        });
      });

      // Feeder'ları toplu ekle
      feeders.forEach(feederData => {
        // addFeeder reducer'ındaki mantığı kopyala (ama console.log olmadan)
        const newFeeder = {
          ...feederData,
          id: feederData.id || generateUniqueId('feeder'),
          sourceId: feederData.sourceId || feederData.from,
          targetId: feederData.targetId || feederData.to,
          from: feederData.from || feederData.sourceId,
          to: feederData.to || feederData.targetId,
          kind: feederData.kind || FEEDER_KINDS.MAIN_FEEDER,
          vertices: feederData.vertices ? [...feederData.vertices] : [],
          electrical: feederData.electrical ? { ...feederData.electrical } : {},
          cable: feederData.cable ? { ...feederData.cable } : {},
          metadata: feederData.metadata ? { ...feederData.metadata } : {},
          protection: feederData.protection ? {
            ...feederData.protection,
            mcb: feederData.protection.mcb ? { ...feederData.protection.mcb } : undefined,
            rcd: feederData.protection.rcd ? { ...feederData.protection.rcd } : undefined
          } : undefined,
          wireId: feederData.wireId
        };

        state.feeders.push(newFeeder);
      });

      state.metadata.updatedAt = new Date().toISOString();
    },

    /**
     * Metadata güncelleme (proje seviyesi bilgiler: tur, vb.)
     */
    updateSimpleSupplyMetadata: (state, action) => {
      state.metadata = {
        ...state.metadata,
        ...action.payload,
        updatedAt: new Date().toISOString()
      };
    }
  }
});

// ===============================
// ACTIONS EXPORT
// ===============================

export const {
  // Basic CRUD
  setSimpleSupplyPoint,
  addComponent,
  updateComponent,
  removeComponent,
  addFeeder,
  updateFeeder,
  removeFeeder,
  addBulk, // ✅ Toplu ekleme

  // Electrical
  incrementalPowerUpdate,
  recalculateAllElectrical,
  recalculateUpstreamChain,

  // High-level
  addLoadToComponent,
  migrateFromLegacy,
  updateSimpleSupplyMetadata,

  // UI
  selectComponent,
  selectFeeder,
  clearSelection
} = simpleSupplySlice.actions;

// ===============================
// SELECTORS
// ===============================

// Basic selectors
export const selectSimpleSupply = (state) => state.simpleSupply;
export const selectComponents = (state) => state.simpleSupply.components;
export const selectFeeders = (state) => state.simpleSupply.feeders;
export const selectMetadata = (state) => state.simpleSupply.metadata;

// Performance selectors
export const selectLastCalculationTime = (state) => state.simpleSupply.lastCalculationTime;
export const selectCalculationCount = (state) => state.simpleSupply.calculationCount;

// Filtered selectors
export const selectComponentsByKind = (kind) => (state) =>
  state.simpleSupply.components.filter(c => c.kind === kind);

export const selectFeedersByType = (type) => (state) =>
  state.simpleSupply.feeders.filter(f => f.type === type);

// Relationship selectors
export const selectComponentById = (id) => (state) =>
  state.simpleSupply.components.find(c => c.id === id);

export const selectFeederById = (id) => (state) =>
  state.simpleSupply.feeders.find(f => f.id === id);

export const selectOutgoingFeeders = (componentId) => (state) =>
  state.simpleSupply.feeders.filter(f => f.from === componentId);

export const selectIncomingFeeders = (componentId) => (state) =>
  state.simpleSupply.feeders.filter(f => f.to === componentId);

// Complex selectors
const selectSimpleSupplyFeeders = (state) => state.simpleSupply.feeders;
const selectSimpleSupplyComponents = (state) => state.simpleSupply.components;
const selectSimpleSupplyMetadata = (state) => state.simpleSupply.metadata;

export const selectSimpleSupplyInstance = createSelector(
  [selectSimpleSupplyFeeders, selectSimpleSupplyComponents, selectSimpleSupplyMetadata],
  (feeders, components, metadata) => new SimpleSupplyPoint({
    feeders,
    components,
    metadata
  })
);

export const selectUpstreamComponents = (startComponentId) => (state) => {
  const simpleSupply = selectSimpleSupplyInstance(state);
  return simpleSupply.getUpstreamComponents(startComponentId);
};

// UI selectors
export const selectSelectedComponent = (state) => {
  const id = state.simpleSupply.selectedComponentId;
  return id ? selectComponentById(id)(state) : null;
};

export const selectSelectedFeeder = (state) => {
  const id = state.simpleSupply.selectedFeederId;
  return id ? selectFeederById(id)(state) : null;
};

// Alias selectors for gradual rename: supplyPoint
export const selectSupplyPoint = selectSimpleSupply;
export const selectSupplyPointInstance = selectSimpleSupplyInstance;

// Default reducer alias
export const supplyPointReducer = simpleSupplySlice.reducer;

export default simpleSupplySlice.reducer;